import axios from "axios";
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getSession } from "next-auth/react";
import { startLoading, endLoading } from "@/store/loading/LoadingSlice";
import { resetAllStoreData } from "@/store/resetActions";
import {
  normalizeFavoritesForPersistence,
  reorderFavorites,
} from "@/lib/utils/favoritesOrderUtils.mjs";

const GET_USER_URL = "/api/user/get-user";
const UPDATE_USER_URL = `${process.env.NEXT_PUBLIC_APP_URL}/api/user/update`;
const UPDATE_USER_ROLE_URL = `${process.env.NEXT_PUBLIC_APP_URL}/api/user/update-user-role`;
const UPDATE_SUBSCRIPTION_URL = `${process.env.NEXT_PUBLIC_APP_URL}/api/user/update-subscription`;
const GET_FINANCIAL_URL = `${process.env.NEXT_PUBLIC_APP_URL}/api/user/financial`;
const UPDATE_LOCATION_URL = `${process.env.NEXT_PUBLIC_APP_URL}/api/user/update-location`;

const initialState = {
  profile: null,
  financial: null,
  subscription: null,
  stats: null,
  trust: null,
  favorites: [],
  savedProfilesMeta: {},
  recentlyViewed: [],
  hasFetched: false,
  role: null, //"seeker",
  isChatOpen: false,
  locationData: null,
  locationUpdatedThisSession: false,
  showSentModal: false,
  sentModalDismissed: false,
  isInitializationComplete: false,
  enrichmentStatus: "idle", // idle, pending, completed, failed
  verificationHandoffActive: false,
  needsEnrichment: null,
  isFullyInitialized: false,
  isUnderReview: false,
  requiresManualInput: false,
  isProcessingBooking: false,
  bookingRequestId: null,
  processingMessage: "",
  processingSharerInfo: null,
  isProcessingState: false,
  isLimited: false,
  limitReason: null,
  limitMessage: null,
  limitDetails: null,
  contactEmailConfirmationRequested: false,
  contactEmailConfirmationCompleted: false,
  bookingEmailHandoffCompleted: false,
  profileReviewModalRequested: false,
  postEnrichmentReplayPending: false,
  connectedAccounts: null,
  verificationComplete: false,
  bookingIdentityVerificationActive: false,
  linkedinCodeVerified: false,
};

export const UserDataSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    getStats: (state, action) => {
      state.stats = action.payload;
    },
    getTrust: (state, action) => {
      state.trust = action.payload;
    },
    setFavorites: (state, action) => {
      state.favorites = action.payload;
    },
    setSavedProfilesMeta: (state, action) => {
      state.savedProfilesMeta = action.payload || {};
    },
    setRecentlyViewed: (state, action) => {
      state.recentlyViewed = Array.isArray(action.payload)
        ? action.payload
        : [];
    },
    setRole: (state, action) => {
      state.role = action.payload;
    },
    getPerson: (state, action) => {
      state.profile = action.payload;
      state.hasFetched = true;
    },
    getFinancial: (state, action) => {
      state.financial = action.payload;
    },
    getSubscription: (state, action) => {
      state.subscription = action.payload;
    },
    updateUser: (state, action) => {
      const { preferences, ...otherData } = action.payload;

      state.profile = {
        ...state.profile,
        ...otherData,
        ...(preferences && {
          preferences: {
            ...state.profile?.preferences,
            ...preferences,
          },
        }),
      };
    },
    updateSubscription: (state, action) => {
      state.subscription = { ...state.subscription, ...action.payload };
    },
    openChat: (state) => {
      state.isChatOpen = true;
    },
    closeChat: (state) => {
      state.isChatOpen = false;
    },
    setUserLocation: (state, action) => {
      state.locationData = action.payload;
    },
    setLocationUpdated: (state) => {
      state.locationUpdatedThisSession = true;
    },
    resetLocationFlag: (state) => {
      state.locationUpdatedThisSession = false;
    },
    showSentModal: (state) => {
      state.showSentModal = true;
      state.sentModalDismissed = false;
    },
    hideSentModal: (state) => {
      state.showSentModal = false;
      state.sentModalDismissed = true;
      state.isInstantBookingFlow = false;
      state.bookingRequestId = null;
    },
    setVerificationStatus: (state, action) => {
      if (!state.profile) state.profile = {};
      if (!state.profile.verifications) state.profile.verifications = {};

      state.profile.verifications = {
        ...state.profile.verifications,
        ...action.payload,
      };

      state.verificationComplete = true;

      const urlParams = new URLSearchParams(window.location.search);
      if (
        urlParams.get("id") &&
        !state.sentModalDismissed &&
        !state.isLimited
      ) {
        state.showSentModal = true;
      }
    },
    resetVerificationStatus: (state) => {
      state.verificationComplete = false;
    },
    setBookingIdentityVerificationActive: (state, action) => {
      state.bookingIdentityVerificationActive = Boolean(action.payload);
    },
    resetModalState: (state) => {
      const urlParams = new URLSearchParams(window.location.search);
      if (!urlParams.get("id")) {
        state.showSentModal = false;
        state.sentModalDismissed = false;
      }
    },
    requestContactEmailConfirmation: (state) => {
      state.contactEmailConfirmationRequested = true;
    },
    clearContactEmailConfirmationRequest: (state) => {
      state.contactEmailConfirmationRequested = false;
    },
    markContactEmailConfirmationCompleted: (state) => {
      state.contactEmailConfirmationCompleted = true;
    },
    markBookingEmailHandoffCompleted: (state) => {
      state.bookingEmailHandoffCompleted = true;
    },
    requestProfileReviewModal: (state, action) => {
      state.profileReviewModalRequested = action.payload || {};
    },
    clearProfileReviewModalRequest: (state) => {
      state.profileReviewModalRequested = null;
    },
    updateProfile: (state, action) => {
      state.profile = {
        ...state.profile,
        ...action.payload,
      };
    },
    setConnectedAccounts: (state, action) => {
      state.connectedAccounts = action.payload;
    },
    setLinkedInCodeVerified: (state, action) => {
      state.linkedinCodeVerified = action.payload === true;
    },
    setInitializationComplete: (state, action) => {
      state.isInitializationComplete = action.payload;
    },
    setEnrichmentStatus: (state, action) => {
      state.enrichmentStatus = action.payload;
    },
    setNeedsEnrichment: (state, action) => {
      state.needsEnrichment = action.payload;
    },
    startEnrichment: (state) => {
      state.enrichmentStatus = "processing";
      state.needsEnrichment = true;
      state.requiresManualInput = false;
      state.isInitializationComplete = false;
      state.enrichmentFailureReason = null;
      state.verificationHandoffActive = true;
    },
    completeEnrichment: (state, action) => {
      state.enrichmentStatus = "completed";
      state.needsEnrichment = false;
      state.requiresManualInput = false;
      state.isInitializationComplete = true;
      state.verificationHandoffActive = false;
      if (action.payload?.profile) {
        state.profile = { ...state.profile, ...action.payload.profile };
      }
    },
    markEnrichmentComplete: (state) => {
      state.enrichmentStatus = "completed";
      state.needsEnrichment = false;
      state.requiresManualInput = false;
      state.isInitializationComplete = true;
      state.verificationHandoffActive = false;
    },
    setUnderReview: (state, action) => {
      state.isUnderReview = action.payload;
    },
    setRequiresManualInput: (state, action) => {
      state.requiresManualInput = action.payload;
    },
    startBookingProcess: (state, action) => {
      state.isProcessingBooking = true;
      state.bookingRequestId = action.payload.requestId || null;
      state.bookingEmailHandoffCompleted = false;
      state.processingMessage = action.payload.message || "Processing...";
      state.processingSharerInfo = action.payload.sharerInfo || null;
      state.showSentModal = true;
      state.sentModalDismissed = false;
      state.isProcessingState = true;
      state.isInstantBookingFlow = action.payload.isInstant || false;
      state.verificationComplete = false;
      // Reset stale limit/error flags from previous attempts so a fresh
      // booking flow can surface its success modal correctly.
      state.isLimited = false;
      state.limitReason = null;
      state.limitMessage = null;
      state.limitDetails = null;
    },
    completeBookingProcess: (state, action) => {
      state.isProcessingBooking = false;
      state.processingMessage = "";
      state.processingSharerInfo = null;
      state.isProcessingState = false;

      if (action.payload?.success) {
        if (action.payload.requestId) {
          state.bookingRequestId = action.payload.requestId;
        }
        state.isLimited = false;
        state.limitReason = null;
        state.limitMessage = null;
        state.limitDetails = null;
        state.showSentModal = true;
      } else {
        state.showSentModal = false;
        state.isInstantBookingFlow = false;
      }
    },
    updateProcessingMessage: (state, action) => {
      state.processingMessage = action.payload;
    },
    setLimitStatus: (state, action) => {
      state.isLimited = action.payload.isLimited;
      state.limitReason = action.payload.limitReason;
      state.limitMessage = action.payload.limitMessage ?? null;
      state.limitDetails = action.payload.limitDetails ?? null;
    },
    failEnrichment: (state, action) => {
      state.enrichmentStatus = "failed";
      state.isInitializationComplete = true;
      state.requiresManualInput = true;
      state.needsEnrichment = false;
      state.enrichmentFailureReason = action.payload?.reason || "unknown";
    },
    resetEnrichmentFailure: (state) => {
      state.enrichmentStatus = "idle";
      state.requiresManualInput = false;
      state.enrichmentFailureReason = null;
    },
    triggerPostEnrichmentReplay: (state) => {
      state.postEnrichmentReplayPending = true;
    },
    clearPostEnrichmentReplay: (state) => {
      state.postEnrichmentReplayPending = false;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(resetAllStoreData, (state) => {
      return { ...initialState };
    });
  },
});

export const {
  setRole,
  getPerson,
  getFinancial,
  getSubscription,
  setFavorites,
  setSavedProfilesMeta,
  setRecentlyViewed,
  updateUser,
  updateSubscription,
  openChat,
  closeChat,
  setUserLocation,
  setLocationUpdated,
  resetLocationFlag,
  setVerificationStatus,
  resetVerificationStatus,
  setBookingIdentityVerificationActive,
  showSentModal,
  hideSentModal,
  resetModalState,
  requestContactEmailConfirmation,
  clearContactEmailConfirmationRequest,
  markContactEmailConfirmationCompleted,
  markBookingEmailHandoffCompleted,
  requestProfileReviewModal,
  clearProfileReviewModalRequest,
  updateProfile,
  setLinkedInCodeVerified,
  setInitializationComplete,
  setEnrichmentStatus,
  setNeedsEnrichment,
  startEnrichment,
  completeEnrichment,
  failEnrichment,
  markEnrichmentComplete,
  setUnderReview,
  getStats,
  setRequiresManualInput,
  startBookingProcess,
  updateProcessingMessage,
  completeBookingProcess,
  setLimitStatus,
  resetEnrichmentFailure,
  triggerPostEnrichmentReplay,
  clearPostEnrichmentReplay,
  setConnectedAccounts,
  getTrust,
} = UserDataSlice.actions;

export const reorderSavedProfiles = (fromIndex, toIndex) => async (
  dispatch,
  getState,
) => {
  const currentFavorites = getState().user?.favorites || [];
  const nextFavorites = reorderFavorites(currentFavorites, fromIndex, toIndex);

  dispatch(setFavorites(nextFavorites));

  try {
    const response = await fetch(
      `${process.env.NEXT_PUBLIC_APP_URL}/api/user/favorites`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          favorites: normalizeFavoritesForPersistence(
            currentFavorites,
            nextFavorites,
          ),
        }),
      },
    );

    if (!response.ok) {
      throw new Error("Failed to reorder saved profiles");
    }

    const data = await response.json();
    if (data?.favorites) {
      dispatch(setFavorites(data.favorites));
    }
  } catch (error) {
    console.error("Failed to reorder saved profiles", error);
  }
};

export const fetchUserData = (force = false, options = {}) => async (
  dispatch,
  getState,
) => {
  const { hasFetched } = getState().user;
  if (hasFetched && !force) return;

  const category = options.silent ? "silent" : "critical";

  dispatch(
    startLoading({
      requestId: "user-data",
      category,
    }),
  );

  try {
    const session = await getSession();
    if (!session?.user?.sub) {
      console.error("No valid session found.");
      return;
    }

    const params = Object.fromEntries(
      new URLSearchParams(window.location.search),
    );

    const response = await axios.get(GET_USER_URL, {
      params,
      headers: {
        Authorization: `Bearer ${session.accessToken}`,
      },
      withCredentials: true,
    });

    // FIRST: Update basic profile and data
    dispatch(getPerson(response.data.profile));
    dispatch(getFinancial(response.data.financial));
    dispatch(getSubscription(response.data.subscription));
    dispatch(getStats(response.data.stats));
    dispatch(getTrust(response.data.trust || null));
    dispatch(setFavorites(response.data.favorites || []));
    dispatch(setSavedProfilesMeta(response.data.savedProfilesMeta || {}));
    dispatch(setRecentlyViewed(response.data.recentlyViewed || []));
    dispatch(
      setConnectedAccounts({
        linkedin: response.data.linkedin || {},
        google: response.data.google || null,
        googleSub: response.data.googleSub || null,
        magicEmail: response.data.magicEmail || null,
        assistantPhone: response.data.assistantPhone || null,
      }),
    );
    dispatch(
      setLinkedInCodeVerified(response.data.linkedinCodeVerified === true),
    );

    const profile = response.data.profile;
    const isInitializationComplete =
      response.data.isInitializationComplete === true;
    const canAutoUpdateLocation =
      isInitializationComplete &&
      response.data.isUnderReview !== true &&
      (!profile?.country_code || !profile?.time_zone);
    if (canAutoUpdateLocation) {
      await dispatch(updateUserLocation());
    }

    const currentState = getState().user;

    if (response.data.enrichmentStatus) {
      const isStaleIdleDuringProcessing =
        currentState.enrichmentStatus === "processing" &&
        response.data.enrichmentStatus === "idle";
      if (
        !isStaleIdleDuringProcessing &&
        (currentState.enrichmentStatus !== "failed" ||
          response.data.enrichmentStatus === "completed")
      ) {
        dispatch(setEnrichmentStatus(response.data.enrichmentStatus));
      }
    }

    if (response.data.needsEnrichment !== undefined) {
      if (currentState.enrichmentStatus !== "failed") {
        dispatch(setNeedsEnrichment(response.data.needsEnrichment));
      }
    }

    if (response.data.isInitializationComplete !== undefined) {
      if (
        currentState.enrichmentStatus !== "failed" ||
        !currentState.requiresManualInput
      ) {
        dispatch(
          setInitializationComplete(response.data.isInitializationComplete),
        );
      }
    }

    if (response.data.requiresManualInput !== undefined) {
      if (
        currentState.enrichmentStatus !== "failed" ||
        currentState.requiresManualInput === false
      ) {
        dispatch(setRequiresManualInput(response.data.requiresManualInput));
      }
    }

    if (
      response.data.enrichmentStatus === "completed" &&
      currentState.enrichmentStatus !== "completed"
    ) {
      dispatch(
        completeEnrichment({
          profile: response.data.profile,
          migrated: response.data.portfolioMigrated || false,
        }),
      );
    }

    // Detect "under review" state early — same batch as setNeedsEnrichment so
    // EnrichmentNotifier never shows the loading overlay for returning users
    // who are already under admin review. The API exposes this via the
    // top-level `isUnderReview` flag (computed server-side from
    // verifications.vanity + moderation.review_id).
    if (response.data.isUnderReview === true) {
      dispatch(setUnderReview(true));
      dispatch(markEnrichmentComplete());
    }

    let newRole;
    if (params.request_id && params.user === "seeker") {
      newRole = "seeker";
    } else if (params.request_id && params.user === "sharer") {
      newRole = "sharer";
    } else {
      newRole = response.data.profile?.role || "seeker";
    }

    dispatch(setRole(newRole));
  } catch (err) {
    console.error("Error in fetchUserData:", err);
    const currentState = getState().user;
    if (
      currentState.enrichmentStatus !== "failed" ||
      !currentState.requiresManualInput
    ) {
      dispatch(failEnrichment({ reason: "fetch_failed" }));
    }
  } finally {
    dispatch(
      endLoading({
        requestId: "user-data",
        category,
      }),
    );
  }
};

export const updateUserLocation = createAsyncThunk(
  "user/updateUserLocation",
  async (_, { dispatch, getState, rejectWithValue }) => {
    try {
      dispatch(
        startLoading({
          requestId: "user-location",
          category: "silent",
        }),
      );

      const { profile } = getState().user;
      const hasTimeZone = profile && profile.time_zone;
      const hasCountryCode = profile && profile.country_code;
      const autoUpdateEnabled =
        profile?.preferences?.auto_update_location === true;

      let locationData = {
        city: null,
        region: null,
        time_zone: hasTimeZone && !autoUpdateEnabled ? profile.time_zone : null,
        country_code:
          hasCountryCode && !autoUpdateEnabled ? profile.country_code : null,
        latitude: null,
        longitude: null,
        postal_code: null,
        source: "client-redux",
        preserve_timezone: hasTimeZone && !autoUpdateEnabled,
        preserve_country_code: hasCountryCode && !autoUpdateEnabled,
      };

      try {
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 5000);

        const response = await fetch(`/api/user/location`, {
          signal: controller.signal,
        });
        clearTimeout(timeout);

        if (response.ok) {
          const data = await response.json();

          locationData = {
            city: data.city || null,
            region: data.region || null,
            time_zone:
              hasTimeZone && !autoUpdateEnabled
                ? profile.time_zone
                : data.timezone || "America/New_York",
            country_code:
              hasCountryCode && !autoUpdateEnabled
                ? profile.country_code
                : data.country || "US",
            latitude: data.loc ? parseFloat(data.loc.split(",")[0]) : null,
            longitude: data.loc ? parseFloat(data.loc.split(",")[1]) : null,
            postal_code: data.postal || null,
            source: "client-redux",
            preserve_timezone: hasTimeZone && !autoUpdateEnabled,
            preserve_country_code: hasCountryCode && !autoUpdateEnabled,
          };
        } else {
          throw new Error(`API returned ${response.status}`);
        }
      } catch (apiError) {
        console.warn("Location API failed, using defaults:", apiError.message);
        locationData = {
          ...locationData,
          time_zone: locationData.time_zone || "America/New_York",
          country_code: locationData.country_code || "US",
        };
      }

      if (!locationData.country_code) locationData.country_code = "US";
      if (!locationData.time_zone) locationData.time_zone = "America/New_York";

      dispatch(setUserLocation(locationData));
      dispatch(setLocationUpdated());

      try {
        await axios.post(UPDATE_LOCATION_URL, locationData, {
          headers: { "Content-Type": "application/json" },
          withCredentials: true,
        });
      } catch (saveError) {
        if (saveError?.response?.status !== 409) {
          console.warn(
            "Failed to save location to backend:",
            saveError.message,
          );
        }
      }

      dispatch(
        updateProfile({
          country_code: locationData.country_code,
          time_zone: locationData.time_zone,
        }),
      );

      return { success: true, locationData };
    } catch (error) {
      console.error("updateUserLocation - COMPLETE ERROR:", error.message);

      const { profile } = getState().user;
      const fallbackData = {
        country_code: profile?.country_code || "US",
        time_zone: profile?.time_zone || "America/New_York",
      };

      dispatch(updateProfile(fallbackData));

      return { success: false, locationData: fallbackData };
    } finally {
      dispatch(
        endLoading({
          requestId: "user-location",
          category: "silent",
        }),
      );
    }
  },
);

export const refreshFinancialData = createAsyncThunk(
  "user/refreshFinancialData",
  async (_, { dispatch, rejectWithValue }) => {
    try {
      dispatch(
        startLoading({
          requestId: "financial-data",
          category: "silent",
        }),
      );

      const response = await axios.get(GET_FINANCIAL_URL, {
        withCredentials: true,
      });

      dispatch(getFinancial(response.data.financial));
      return response.data.financial;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Server Error");
    } finally {
      dispatch(
        endLoading({
          requestId: "financial-data",
          category: "silent",
        }),
      );
    }
  },
);

export const saveUserData = createAsyncThunk(
  "user/saveUserData",
  async (userData, { dispatch, getState, rejectWithValue }) => {
    const { silentUpdate, ...dataToSend } = userData;

    try {
      const category = silentUpdate ? "silent" : "critical";
      dispatch(
        startLoading({
          requestId: "save-user-data",
          category,
        }),
      );

      const response = await axios.put(UPDATE_USER_URL, dataToSend, {
        headers: { "Content-Type": "application/json" },
        withCredentials: true,
      });

      dispatch(updateUser(dataToSend));
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Server Error");
    } finally {
      const category = silentUpdate ? "silent" : "critical";
      dispatch(
        endLoading({
          requestId: "save-user-data",
          category,
        }),
      );
    }
  },
);

export const saveUserSubscription = createAsyncThunk(
  "user/saveUserSubscription",
  async (subscriptionData, { dispatch, rejectWithValue }) => {
    const { silentUpdate, ...dataToSend } = subscriptionData;

    try {
      const category = silentUpdate ? "silent" : "critical";
      dispatch(
        startLoading({
          requestId: "save-subscription",
          category,
        }),
      );

      const response = await axios.put(UPDATE_SUBSCRIPTION_URL, dataToSend, {
        headers: { "Content-Type": "application/json" },
        withCredentials: true,
      });

      dispatch(updateSubscription(dataToSend));
      return response.data;
    } catch (error) {
      console.error("Failed to save subscription data:", error);
      return rejectWithValue(error.response?.data?.message || "Server Error");
    } finally {
      const category = silentUpdate ? "silent" : "critical";
      dispatch(
        endLoading({
          requestId: "save-subscription",
          category,
        }),
      );
    }
  },
);

export const updateUserRole = createAsyncThunk(
  "user/updateUserRole",
  async (newRole, { dispatch, rejectWithValue }) => {
    try {
      dispatch(
        startLoading({
          requestId: "update-role",
          category: "critical",
        }),
      );

      const response = await axios.put(
        UPDATE_USER_ROLE_URL,
        { role: newRole },
        {
          headers: { "Content-Type": "application/json" },
          withCredentials: true,
        },
      );

      dispatch(setRole(newRole));
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || "Server Error");
    } finally {
      dispatch(
        endLoading({
          requestId: "update-role",
          category: "critical",
        }),
      );
    }
  },
);

export const toggleFavoriteProfile = (profileSub) => async (
  dispatch,
  getState,
) => {
  const { favorites } = getState().user;
  const isCurrentlyFavorited = favorites?.includes(profileSub);

  // Optimistic update
  const optimisticFavorites = isCurrentlyFavorited
    ? favorites.filter((s) => s !== profileSub)
    : [...(favorites || []), profileSub];
  dispatch(setFavorites(optimisticFavorites));

  try {
    const response = await fetch(
      `${process.env.NEXT_PUBLIC_APP_URL}/api/user/favorites`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ profileSub }),
      },
    );
    if (!response.ok) throw new Error("Failed");
    const data = await response.json();
    dispatch(setFavorites(data.favorites));
    dispatch(setSavedProfilesMeta(data.savedProfilesMeta || {}));
  } catch {
    dispatch(setFavorites(favorites || []));
  }
};

export const recordRecentlyViewed = (profileSub) => async (
  dispatch,
  getState,
) => {
  if (!profileSub) return;

  const previous = getState().user.recentlyViewed || [];
  const optimisticEntry = {
    profileSub,
    viewedAt: new Date().toISOString(),
  };
  const optimistic = [
    optimisticEntry,
    ...previous.filter((entry) => entry?.profileSub !== profileSub),
  ].slice(0, 21);
  dispatch(setRecentlyViewed(optimistic));

  try {
    const response = await fetch(
      `${process.env.NEXT_PUBLIC_APP_URL}/api/user/recently-viewed`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ profileSub }),
      },
    );

    if (!response.ok)
      throw new Error("Failed to record recently viewed profile");
    const data = await response.json();
    dispatch(setRecentlyViewed(data.recentlyViewed || optimistic));
  } catch (error) {
    console.error("Failed to record recently viewed profile", error);
    dispatch(setRecentlyViewed(previous));
  }
};

export const removeRecentlyViewed = (profileSub) => async (
  dispatch,
  getState,
) => {
  const previous = getState().user?.recentlyViewed || [];
  dispatch(
    setRecentlyViewed(
      previous.filter((entry) => entry?.profileSub !== profileSub),
    ),
  );

  try {
    const response = await fetch(
      `${process.env.NEXT_PUBLIC_APP_URL}/api/user/recently-viewed`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ profileSub, action: "remove" }),
      },
    );

    if (!response.ok)
      throw new Error("Failed to remove recently viewed profile");
    const data = await response.json();
    dispatch(setRecentlyViewed(data.recentlyViewed || []));
  } catch (error) {
    console.error("Failed to remove recently viewed profile", error);
    dispatch(setRecentlyViewed(previous));
  }
};

export default UserDataSlice.reducer;
